import { useCallback, useReducer, useState } from 'react';
import type {
  LayoutChangeEvent,
  NativeSyntheticEvent,
  TextLayoutEventData,
} from 'react-native';

interface OverflowMeasurements {
  collapsedHeight: number;
  fullHeight: number;
  fullLineCount: number;
}

type OverflowMeasurementsAction =
  | { height: number; type: 'collapsed-height' }
  | { height: number; type: 'full-height' }
  | { lineCount: number; type: 'full-line-count' };

const initialMeasurements: OverflowMeasurements = {
  collapsedHeight: 0,
  fullHeight: 0,
  fullLineCount: 0,
};

const measurementsReducer = (
  state: OverflowMeasurements,
  action: OverflowMeasurementsAction,
): OverflowMeasurements => {
  switch (action.type) {
    case 'collapsed-height':
      return { ...state, collapsedHeight: action.height };
    case 'full-height':
      return { ...state, fullHeight: action.height };
    case 'full-line-count':
      return { ...state, fullLineCount: action.lineCount };
  }
};

export interface ExpandableText {
  /** True when the full text overflows the collapsed line limit. */
  canExpand: boolean;
  isExpanded: boolean;
  /** `onLayout` for the visible, line-clamped text. */
  onCollapsedTextLayout: (event: LayoutChangeEvent) => void;
  /** `onLayout` for the off-screen, unclamped mirror of the text. */
  onFullTextHeightLayout: (event: LayoutChangeEvent) => void;
  /** `onTextLayout` for the off-screen, unclamped mirror of the text. */
  onFullTextLineLayout: (
    event: NativeSyntheticEvent<TextLayoutEventData>,
  ) => void;
  toggle: () => void;
}

/**
 * Expand / collapse state for text truncated to a fixed number of lines,
 * with overflow detection so a "Show more" toggle is only offered when the
 * text actually exceeds the limit.
 *
 * Overflow is detected from an off-screen, unclamped mirror of the text:
 * native reports the mirror's laid-out line count via `onTextLayout`, while
 * web — where `onTextLayout` never fires — falls back to comparing the
 * mirror's `onLayout` height against the collapsed text's height.
 *
 * @param collapsedLineLimit - Line count the text is clamped to while
 * collapsed; must match the `numberOfLines` applied to the visible text.
 * @returns Expand state plus layout handlers; callers render the text twice
 * (visible + mirror) with the returned handlers attached.
 */
export const useExpandableText = (
  collapsedLineLimit: number,
): ExpandableText => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [measurements, dispatchMeasurement] = useReducer(
    measurementsReducer,
    initialMeasurements,
  );

  const canExpand =
    measurements.fullLineCount > collapsedLineLimit ||
    (measurements.collapsedHeight > 0 &&
      measurements.fullHeight > measurements.collapsedHeight);

  const toggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const onCollapsedTextLayout = useCallback(
    (event: LayoutChangeEvent) => {
      // While expanded the text is unclamped, so its height must not
      // overwrite the collapsed measurement.
      if (isExpanded) {
        return;
      }
      dispatchMeasurement({
        height: event.nativeEvent.layout.height,
        type: 'collapsed-height',
      });
    },
    [isExpanded],
  );

  const onFullTextHeightLayout = useCallback((event: LayoutChangeEvent) => {
    dispatchMeasurement({
      height: event.nativeEvent.layout.height,
      type: 'full-height',
    });
  }, []);

  const onFullTextLineLayout = useCallback(
    (event: NativeSyntheticEvent<TextLayoutEventData>) => {
      dispatchMeasurement({
        lineCount: event.nativeEvent.lines.length,
        type: 'full-line-count',
      });
    },
    [],
  );

  return {
    canExpand,
    isExpanded,
    onCollapsedTextLayout,
    onFullTextHeightLayout,
    onFullTextLineLayout,
    toggle,
  };
};
